import { Color } from '@coast/schemas';
import { isEmpty, isNil, uniqBy } from 'lodash';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBCreateTagRow } from '@shared/components/blocks/lib/tags/createTagRow';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { Pill } from '@shared/components/pill';
import { TagField } from '@shared/data/entity/fields/TagField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { EditFieldContainer } from '../EditFieldContainer';
import { useAddTag } from './useAddTag';
import { useUpdateTag } from './useUpdateTag';

interface Props {
  field: TagField | null;
  component:
    | MBAPI.WorkflowTemplate_components_TagComponent
    | MBAPI.WorkflowTemplate_components_RelatedCardLookupComponent;
  workflowTemplateId: string;
  updateField?: (componentId: string, field: TagField | null) => void;
  error?: string;
  allowCreate?: boolean;
  allowClear?: boolean;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function TagEditField({
  field,
  component,
  updateField,
  workflowTemplateId,
  error,
  allowCreate,
  allowClear,
  showIcon,
  label,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const { addTag } = useAddTag(workflowTemplateId, component.id);
  const { updateTag } = useUpdateTag(workflowTemplateId, component.id);
  const [recentlyCreatedTagOptions, setRecentlyCreatedTagOptions] = useState<
    MBAPI.WorkflowTemplate_components_TagComponent_options[]
  >([]);
  const tagOptions = useMemo(
    () => (isTagComponent(component) ? component.options ?? [] : field?.tagOptions ?? []),
    [component, field],
  );
  const isMultiselect = isTagComponent(component)
    ? component.multiselect ?? false
    : field?.multiselect ?? false;
  const addAdditionalValues = isTagComponent(component)
    ? component.addAdditionalValues ?? false
    : field?.addAdditionalValues ?? false;
  const componentOptions = useMemo(() => {
    return uniqBy([...recentlyCreatedTagOptions, ...tagOptions], t => t.value);
  }, [tagOptions, recentlyCreatedTagOptions]);
  const options = useMemo(
    () => componentOptions.filter(option => !option.archived) ?? [],
    [componentOptions],
  );
  const archived = useMemo(
    () => componentOptions.filter(option => !!option.archived) ?? [],
    [componentOptions],
  );
  const optionValues = useMemo(
    () => componentOptions.map(option => option.value),
    [componentOptions],
  );
  const archivedValues = useMemo(() => archived.map(option => option.value), [archived]);

  const labelExtractor = useCallback(
    value => componentOptions.find(option => option.value === value)?.label ?? '',
    [componentOptions],
  );

  const selectedTags = useMemo<MBAPI.WorkflowTemplate_components_TagComponent_options[]>(() => {
    const selected = field?.tagIds ?? [];
    return selected
      .map((id: string) => componentOptions.find(item => item.value === id))
      .filter(notNil);
  }, [field?.tagIds, componentOptions]);

  const onChange = useCallback(
    (selection: string[]) => {
      updateField?.(
        component.id,
        !isNil(selection) && !isEmpty(selection)
          ? new TagField(selection, component.id, tagOptions, isMultiselect, addAdditionalValues)
          : null,
      );
    },
    [component.id, tagOptions, isMultiselect, addAdditionalValues, updateField],
  );

  const renderItem = useCallback(
    (value: string) => {
      const option = componentOptions.find(item => item.value === value);
      return <SelectModalItem option={option} />;
    },
    [componentOptions],
  );

  const createAndSelectNew = useCallback(
    async (value: string) => {
      try {
        const result = await addTag(value);
        if (!isNil(result)) {
          const newSelectedTags = isMultiselect
            ? [...selectedTags.map(tag => tag.value), result.value]
            : [result.value];
          setRecentlyCreatedTagOptions([...recentlyCreatedTagOptions, result]);
          updateField?.(
            component.id,
            new TagField(
              newSelectedTags,
              component.id,
              [...tagOptions, result],
              isMultiselect,
              addAdditionalValues,
            ),
          );
        }
      } catch (err) {
        // don't care
      }
    },
    [
      addTag,
      component.id,
      isMultiselect,
      tagOptions,
      recentlyCreatedTagOptions,
      selectedTags,
      updateField,
      addAdditionalValues,
    ],
  );

  const match = useCallback(
    (value: string, query: string, exact: boolean) => {
      const labelToMatch = labelExtractor(value);
      if (isNil(labelToMatch)) {
        return false;
      }
      if (exact) {
        return labelToMatch.toLowerCase() === query?.toLowerCase();
      }
      return labelToMatch.toLowerCase().includes(query?.toLowerCase());
    },
    [labelExtractor],
  );

  const renderCreateNew = useCallback(
    (query: string, creating: boolean) => {
      if (creating) {
        return (
          <View style={{ height: grid(8) }} center>
            <MBLoadingIndicator />
          </View>
        );
      }

      return <MBCreateTagRow query={query} numberOfOptions={options.length} />;
    },
    [options.length],
  );

  const onCreate = allowCreate ? createAndSelectNew : undefined;

  const onChangeLabel = useCallback(
    async (value: string, newLabel: string) => {
      updateTag(value, { label: newLabel });
    },
    [updateTag],
  );

  const onArchive = useCallback(
    async (value: string) => {
      await updateTag(value, { archived: true });
      setRecentlyCreatedTagOptions(prev => prev.filter(option => option.value !== value));
    },
    [updateTag],
  );

  const onUnarchive = useCallback(
    async (value: string) => {
      updateTag(value, { archived: false });
    },
    [updateTag],
  );

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const hasTags = selectedTags.length > 0;
  const isReadOnly = !!component.readonly || isNil(updateField);
  const showClearButton = hasTags && !isReadOnly && allowClear;
  const anchorName = `--tag-edit-field-${component.id}`;

  if (isReadOnly) {
    return (
      <TagFieldSummary
        component={component}
        anchorName={anchorName}
        selectedTags={selectedTags}
        hasTags={hasTags}
        isReadOnly={isReadOnly}
        showIcon={showIcon}
        label={label}
        showLabel={showLabel}
        reserveIconSpace={reserveIconSpace}
        compactTop={compactTop}
        compactBottom={compactBottom}
        subfooter={subfooter}
        isGrayScale={isGrayScale}
        isItalic={isItalic}
        style={style}
      />
    );
  }

  return (
    <MBMultiSelectModal<string>
      label={component.label ?? ''}
      anchorName={anchorName}
      onChange={onChange}
      onChangeLabel={onChangeLabel}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onCreate={onCreate}
      selectAll
      selectAllLabel={i18n('card.blocks.tagsInput.selectAllLabel')}
      renderItem={renderItem}
      renderArchivedItem={renderItem}
      values={optionValues}
      selectedValues={selectedTags?.map(t => t.value)}
      labelExtractor={labelExtractor}
      match={match}
      renderCreateNew={renderCreateNew}
      archivedValues={archivedValues}
      itemLabel={i18n('card.blocks.tagsInput.itemLabel')}
      itemLabelPlural={i18n('card.blocks.tagsInput.itemLabelPlural')}
      errorLabel={i18n('errors.tag.enterName')}
      itemsEditable={addAdditionalValues}
      max={isMultiselect ? undefined : 1}
    >
      <TagFieldSummary
        component={component}
        anchorName={anchorName}
        selectedTags={selectedTags}
        hasTags={hasTags}
        onClear={showClearButton ? onClear : undefined}
        error={error}
        isReadOnly={isReadOnly}
        showIcon={showIcon}
        label={label}
        showLabel={showLabel}
        indentLabel={indentLabel}
        reserveIconSpace={reserveIconSpace}
        compactTop={compactTop}
        compactBottom={compactBottom}
        subfooter={subfooter}
        isGrayScale={isGrayScale}
        isItalic={isItalic}
        style={style}
      />
    </MBMultiSelectModal>
  );
}

function TagFieldSummary({
  component,
  anchorName,
  selectedTags,
  onClear,
  hasTags,
  error,
  isReadOnly,
  showIcon,
  label,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: {
  component:
    | MBAPI.WorkflowTemplate_components_TagComponent
    | MBAPI.WorkflowTemplate_components_RelatedCardLookupComponent;
  anchorName: string;
  selectedTags: MBAPI.WorkflowTemplate_components_TagComponent_options[];
  onClear?: () => void;
  hasTags: boolean;
  error?: string;
  isReadOnly: boolean;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  isGrayScale?: boolean;
  isItalic?: boolean;
  subfooter?: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const responsiveForms = useResponsiveFormContext();
  const showClearButton = hasTags && !isReadOnly;
  const shouldShowLabel = !isNil(showLabel) ? showLabel : responsiveForms || hasTags;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={showIcon}
      label={label}
      showLabel={shouldShowLabel}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      showPlaceholder={!hasTags}
      showClearButton={showClearButton}
      onClear={onClear}
      error={error}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
    >
      {hasTags && (
        <MBView style={styles.tagList}>
          {selectedTags.map(tag => (
            <Tag
              key={tag.value}
              label={tag.label}
              color={tag.backgroundColor as Color}
              archived={!!tag.archived}
              style={styles.pill}
            />
          ))}
        </MBView>
      )}
    </EditFieldContainer>
  );
}

function SelectModalItem({
  option,
}: {
  option?: MBAPI.WorkflowTemplate_components_TagComponent_options;
}) {
  if (isNil(option)) {
    return null;
  }
  return (
    <View row centerVertically flex>
      <Pill
        key={option?.value}
        backgroundColor={
          option?.archived ? 'backgroundGrayLight' : (option?.backgroundColor as Color)
        }
        round
        italic={option?.archived ?? false}
        style={styles.pill}
      >
        {option?.label}
      </Pill>
    </View>
  );
}

const styles = StyleSheet.create({
  tagList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingVertical: grid(0),
    // cheat rounded tags left a bit (optical alignment)
    marginLeft: -grid(1),
  },
  pill: {
    marginLeft: grid(0.5),
  },
});
